import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Upload, Send, Save } from "lucide-react";

export default function ResearchReportsPanel({ onChanged }) {
  const [reports, setReports] = useState([]);

  const load = async () => {
    const r = await api.get("/api/admin/research/reports");
    setReports(r.data.reports || []);
    return r.data.reports || [];
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="research-reports-panel">
      {reports.map((r) => (
        <ReportCard key={r.version} report={r} reload={load} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ReportCard({ report, reload, onChanged }) {
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [ttl, setTtl] = useState(report.ttl_hours);
  const [maxDl, setMaxDl] = useState(report.max_downloads);
  const [msg, setMsg] = useState("");
  const v = report.version;

  const poll = async () => {
    for (let i = 0; i < 120; i++) {
      const list = await reload();
      const cur = list.find((x) => x.version === v);
      if (cur?.upload_status !== "processing") return cur;
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setMsg("");
    try {
      const init = await api.post(`/api/admin/research/reports/${v}/upload/init`, { filename: file.name, size: file.size });
      const { upload_id, chunk_size } = init.data;
      const total = Math.ceil(file.size / chunk_size);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("index", i);
        fd.append("file", file.slice(i * chunk_size, (i + 1) * chunk_size), "chunk");
        await api.post(`/api/admin/research/reports/${v}/upload/${upload_id}/chunk`, fd);
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const fd = new FormData();
      fd.append("filename", file.name);
      await api.post(`/api/admin/research/reports/${v}/upload/${upload_id}/complete`, fd);
      const cur = await poll();
      setMsg(cur?.upload_status === "error" ? `Ошибка: ${cur.upload_error}` : "Файл загружен");
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveSettings = async () => {
    try {
      await api.put(`/api/admin/research/reports/${v}/settings`, { ttl_hours: Number(ttl), max_downloads: Number(maxDl) });
      setMsg("Настройки сохранены (применяются к новым ссылкам)");
      reload();
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail));
    }
  };

  const sendPending = async () => {
    if (!window.confirm(`Отправить ссылки ${report.pending_count} ожидающим заявкам?`)) return;
    try {
      const r = await api.post(`/api/admin/research/reports/${v}/send-pending`);
      setMsg(`Рассылка запущена: ${r.data.queued} писем. Обновите таблицу через минуту.`);
      setTimeout(() => { reload(); onChanged?.(); }, 5000);
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="border border-black/10 p-5 space-y-4" data-testid={`report-card-${v}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-mono">{report.pages_label}</div>
          <div className="text-xl font-medium">{report.title}</div>
        </div>
        <span className={`label-mono px-2 py-1 border ${report.available ? "border-black" : "border-black/20 text-foreground/50"}`}
          data-testid={`report-status-${v}`}>
          {report.upload_status === "processing" ? "Обработка…" : report.available ? "Загружен" : "Нет файла"}
        </span>
      </div>
      {report.available && (
        <div className="text-xs text-foreground/60" data-testid={`report-file-${v}`}>
          {report.original_filename} · {(report.size_bytes / 1024 / 1024).toFixed(1)} МБ · {(report.uploaded_at || "").slice(0, 16).replace("T", " ")}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} data-testid={`report-file-input-${v}`} />
        <button className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={progress !== null}
          data-testid={`report-upload-btn-${v}`}>
          <Upload size={14} /> {progress !== null ? `Загрузка ${progress}%` : report.available ? "Заменить PDF" : "Загрузить PDF"}
        </button>
        <button className="btn-primary" onClick={sendPending} disabled={!report.available || !report.pending_count}
          data-testid={`report-send-pending-${v}`}>
          <Send size={14} /> Разослать ожидающим ({report.pending_count})
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs">
          <div className="label-mono mb-1">Срок, ч</div>
          <input type="number" min={1} className="field-input w-24" value={ttl} onChange={(e) => setTtl(e.target.value)}
            data-testid={`report-ttl-${v}`} />
        </label>
        <label className="text-xs">
          <div className="label-mono mb-1">Скачиваний</div>
          <input type="number" min={1} className="field-input w-24" value={maxDl} onChange={(e) => setMaxDl(e.target.value)}
            data-testid={`report-maxdl-${v}`} />
        </label>
        <button className="btn-ghost" onClick={saveSettings} data-testid={`report-save-settings-${v}`}><Save size={14} /> Сохранить</button>
      </div>
      <div className="text-xs text-foreground/55">Отправлено ссылок: {report.sent_count}</div>
      {msg && <div className="text-sm" data-testid={`report-msg-${v}`}>{msg}</div>}
    </div>
  );
}
